import * as React from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import { HolderOutlined } from '@ant-design/icons';
import { Destination } from '@/types/destinations';
import { DraggedSteps, StepDropTarget, dropTargetId } from './reorderStepGraphHelpers';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { antGrey, runwayColors } from '../styles/globalStyles';

// A button, not a bare icon, so a row is exactly as tall as it is with the settings cog. isHidden
// keeps that height on the rows that cannot be dragged. The row itself is the drag source, so this
// takes no pointer events of its own.
export const StepDragHandle: React.FC<{ isHidden?: boolean }> = ({ isHidden }) => (
    <Button
        type="link"
        icon={<HolderOutlined />}
        style={{
            cursor: 'grab',
            color: antGrey.grey7,
            pointerEvents: 'none',
            visibility: isHidden ? 'hidden' : undefined,
        }}
    />
);

export const DraggableStepRow: React.FC<{
    stepConfigId: string;
    dragged: DraggedSteps;
    dropTarget?: StepDropTarget;
    children: React.ReactNode;
}> = ({ stepConfigId, dragged, dropTarget, children }) => {
    const {
        attributes,
        listeners,
        setNodeRef: setDragRef,
        isDragging,
    } = useDraggable({
        id: stepConfigId,
        data: dragged,
    });
    const { setNodeRef: setDropRef, isOver } = useDroppable({
        id: dropTarget ? dropTargetId(dropTarget) : `${stepConfigId}-not-droppable`,
        disabled: !dropTarget,
    });

    return (
        <div
            ref={(node) => {
                setDragRef(node);
                setDropRef(node);
            }}
            style={{
                cursor: 'grab',
                userSelect: 'none',
                touchAction: 'none',
                // The row keeps its slot while StepDragPreview follows the cursor.
                opacity: isDragging ? 0.4 : 1,
                // Dragging leaves the row focused, and the ring reads as a selection that will not go away.
                outline: 'none',
                // Inset, so marking the landing edge does not move the row.
                boxShadow: isOver ? `inset 0 2px 0 ${runwayColors.primary}` : undefined,
            }}
            {...attributes}
            {...listeners}
        >
            {children}
        </div>
    );
};

// A layer over the whole label cell is the drag source, so a group can be picked up anywhere on it
// without the content having to stretch to fill a table cell.
export const DraggableGroupLabel: React.FC<{ id: string; dragged: DraggedSteps; children: React.ReactNode }> = ({
    id,
    dragged,
    children,
}) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, data: dragged });

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 8,
                    opacity: isDragging ? 0.4 : 1,
                }}
            >
                {children}
                <HolderOutlined style={{ color: antGrey.grey7, marginTop: 4 }} />
            </div>
            <div
                ref={setNodeRef}
                style={{
                    position: 'absolute',
                    inset: 0,
                    cursor: 'grab',
                    outline: 'none',
                    userSelect: 'none',
                    touchAction: 'none',
                }}
                {...attributes}
                {...listeners}
            />
        </>
    );
};

// Holds the section a group drag lifts, which DragOverlay would otherwise size from the label cell.
export const GroupDragPreview: React.FC<{ width: number; children: React.ReactNode }> = ({ width, children }) => (
    <div
        style={{
            width,
            cursor: 'grabbing',
            borderRadius: 8,
            backgroundColor: antGrey.grey1,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
        }}
    >
        {children}
    </div>
);

// Sized by DragOverlay from the row it was picked up from, so it fills its box.
export const StepDragPreview: React.FC<{ stepName: string; destinations: Destination[] }> = ({
    stepName,
    destinations,
}) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            width: '100%',
            height: '100%',
            padding: '0 20px',
            cursor: 'grabbing',
            backgroundColor: antGrey.grey1,
            border: `1px solid ${antGrey.grey4}`,
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
        }}
    >
        {destinations.length > 0 && <DestinationCounter size={20} destinations={destinations} />}
        <Text>{stepName}</Text>
    </div>
);

export const StepDropZone: React.FC<{ target: StepDropTarget; label: string }> = ({ target, label }) => {
    const { setNodeRef, isOver } = useDroppable({ id: dropTargetId(target) });

    return (
        <div
            ref={setNodeRef}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // Matches the small Add step button it stands in for, so swapping them moves nothing.
                height: 24,
                borderRadius: 8,
                border: `1px dashed ${isOver ? runwayColors.primary : antGrey.grey5}`,
                backgroundColor: isOver ? antGrey.grey3 : undefined,
            }}
        >
            <Text type="secondary" style={{ fontSize: 12 }}>
                {label}
            </Text>
        </div>
    );
};
